import React, { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse, useLocation } from 'react-router-dom';
import { UnauthorizedError } from './UnauthorizedError';
import { NotFoundError } from './NotFoundError';
import { ServerErrorPage } from './ServerErrorPage';
import SentryService from '../../services/SentryService';

export function RootErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();

  // Report error to Sentry with context
  useEffect(() => {
    if (error) {
      // Add route information to the error
      SentryService.setTag('route', location.pathname);
      SentryService.setTag(
        'error_type',
        isRouteErrorResponse(error) ? 'route_error' : 'runtime_error',
      );

      // Add additional context based on error type
      if (isRouteErrorResponse(error)) {
        SentryService.setContext('route_error', {
          status: error.status,
          statusText: error.statusText,
          path: location.pathname,
          search: location.search,
        });

        // For route errors, create a proper Error object to send to Sentry
        const routeError = new Error(`Route error: ${error.statusText || error.status}`);
        SentryService.captureException(routeError, {
          tags: {
            component: 'RootErrorBoundary',
            status: error.status.toString(),
          },
          extra: {
            routeError: {
              status: error.status,
              statusText: error.statusText,
              data: error.data,
            },
          },
        });
      } else if (error instanceof Error) {
        SentryService.setContext('error_details', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });

        // For standard Error objects, capture them directly
        SentryService.captureException(error, {
          tags: {
            component: 'RootErrorBoundary',
          },
        });
      } else {
        // For any other type of error, create a proper Error object
        const unknownError = new Error('Unknown error occurred');
        SentryService.captureException(unknownError, {
          tags: {
            component: 'RootErrorBoundary',
            error_type: 'unknown',
          },
          extra: {
            originalError: error,
          },
        });
      }
    }
  }, [error, location]);

  // Handle HTTP errors from React Router
  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 401:
        return (
          <UnauthorizedError
            message="You need to be authenticated to access this page."
            cta="Please log in to continue."
          />
        );

      case 403:
        return (
          <UnauthorizedError
            message="You don't have permission to access this page."
            cta="Please contact your administrator if you believe this is an error."
          />
        );

      case 404:
        return <NotFoundError />;

      default:
        return (
          <ServerErrorPage
            status={error.status}
            message={error.statusText || `An error occurred (${error.status})`}
          />
        );
    }
  }

  // Handle unexpected JS errors
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  const errorStack = error instanceof Error ? error.stack : undefined;

  return <ServerErrorPage message={errorMessage} stack={errorStack} />;
}
